import { ReactElement, SetStateAction, Dispatch, useState } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Typography, Chip } from '@material-ui/core'
import { Edit, DateRange, Schedule, Cancel } from '@material-ui/icons'

import { webUrl } from '../../utils/env'
import { timezones } from '../../utils/timezones'
import { SingleBookingDetailType, BookingModalDetailsType } from '../../types/bookings'
import { CANCEL_BOOKINGS_REQUEST } from '../../redux/constants'
import { ConfirmationDialog } from '..'
import { useInfoByMessageId } from '../../hooks/useInfoByMessageId'
import restingPerchie from '../../assets/resting-perchie.png'
import { bookingTypes } from '../../utils/constants'

type BookingCardProps = {
  booking: SingleBookingDetailType
  setOpen: () => void
  setBookingModalDetails: Dispatch<SetStateAction<BookingModalDetailsType>>
}

const useStyles = makeStyles(() => ({
  individualBookingContainer: {
    minHeight: 180,
    width: '95%',
    display: 'flex',
    margin: '15px auto',
    borderBottom: '2px solid lightgrey',
    borderRadius: 4,
    padding: '10px 0 10px 10px',
    boxSizing: 'border-box'
  },
  rightContainer: {
    marginLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%'
  },
  propertyImg: {
    maxHeight: 192,
    minWidth: 192,
    maxWidth: 192
  },
  timingContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  timing: {
    marginLeft: 5,
    fontWeight: 500
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  buttonWidth: {
    minWidth: 45
  },
  viewButton: {
    paddingLeft: 0
  }
}))

export const BookingCard = ({ booking, setOpen, setBookingModalDetails }: BookingCardProps): ReactElement => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { customer } = useInfoByMessageId()
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false)

  const bookingType = bookingTypes.find((bookingType) => bookingType.value === booking.type)

  const linkToProperty = () => {
    if (booking.linked_property_id) window.open(`${webUrl}/properties/${booking.linked_property_id}`, '_blank')
  }

  const openUrl = () => {
    if (booking.url.startsWith('http')) {
      window.open(booking.url, '_blank')
    } else {
      window.open(`https://${booking.url}`, '_blank')
    }
  }

  const bookingDetails = () => {
    const bookingTime = booking.booking_time.includes(':')
      ? booking.booking_time
      : `${booking.booking_time.substring(0, 2)}:${booking.booking_time.substring(2)}`

    const existingTimezone = timezones.find((zone) => zone.selectValue === booking.timezone)

    return {
      address: booking.address || booking.property_title || '',
      agent_name: booking.agent_name || '',
      booking_id: booking.booking_id || '',
      contact_number: booking.contact_number || '',
      dateTime: `${booking.booking_date} ${bookingTime}`,
      note: booking.note || '',
      property_id: booking.linked_property_id || '',
      tz: existingTimezone || { selectValue: 'Europe/London', displayValue: '(GMT+01:00) London' },
      title: booking.title || '',
      type: booking.type || 'property_visits',
      url: booking.url || ''
    }
  }

  const editBooking = () => {
    const allDetails = bookingDetails()
    setBookingModalDetails({ ...allDetails })
    setOpen()
  }

  const cancelBooking = () => {
    if (customer) {
      const allDetails = bookingDetails()
      const newBookingDetails = { ...allDetails, tz: allDetails.tz?.selectValue, status: 'cancelled' }
      dispatch({
        type: CANCEL_BOOKINGS_REQUEST,
        payload: { userId: customer.linked_user_id, bookingId: booking.booking_id, newBookingDetails }
      })
    }
  }

  return (
    <>
      <div className={classes.individualBookingContainer}>
        <img
          data-testid="booking-thumbnail"
          src={booking.property_featured_image || bookingType?.img || restingPerchie}
          alt="property img"
          onClick={linkToProperty}
          style={{ cursor: booking.linked_property_id ? 'pointer' : 'default' }}
          className={classes.propertyImg}
        />
        <div className={classes.rightContainer}>
          <div className={classes.timingContainer}>
            <Chip label={bookingType?.title || 'Property Visits'} color="primary" style={{ width: 'fit-content' }} />
            <div className={classes.buttonContainer}>
              <Button
                size="small"
                onClick={editBooking}
                classes={{ root: classes.buttonWidth }}
                data-testid="edit-booking-btn"
              >
                <Edit color="secondary" />
              </Button>
              <Button
                size="small"
                onClick={() => setOpenConfirmationDialog(true)}
                classes={{ root: classes.buttonWidth }}
                data-testid="cancel-booking-btn"
              >
                <Cancel style={{ color: 'grey' }} />
              </Button>
            </div>
          </div>
          <div className={classes.timingContainer}>
            <div style={{ display: 'flex' }}>
              <DateRange />
              <Typography variant="subtitle1" className={classes.timing}>
                {booking.booking_date}
              </Typography>
              <Schedule style={{ marginLeft: 15 }} />
              <Typography variant="subtitle1" className={classes.timing}>
                {booking.booking_time.includes(':')
                  ? booking.booking_time.substring(0, 5)
                  : `${booking.booking_time.substring(0, 2)}:${booking.booking_time.substring(2, 4)}`}
              </Typography>
            </div>
          </div>
          <div>
            <Typography variant="body2" style={{ wordBreak: 'break-word' }}>
              <b>Address:</b> {booking.address || booking.property_title}
            </Typography>
          </div>
          <div>
            <Button
              data-testid="gmap-button"
              size="small"
              variant="outlined"
              disabled={!booking.url ? true : false}
              onClick={openUrl}
            >
              {booking.url && booking.url.includes('google') ? 'View on G-Map' : 'View URL'}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmationDialog
        open={openConfirmationDialog}
        setOpen={() => setOpenConfirmationDialog(false)}
        message="Are you sure you want to cancel this booking?"
        onConfirm={cancelBooking}
        trackingContext="booking-delete"
      />
    </>
  )
}
